import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import type { MuscleStatus } from "@/lib/fitnessBodyCharge";
import { getSessionKind } from "@/lib/fitness/sessionKind";
import { FITNESS_WORKOUT_FINISH_LIMITS as LIMITS } from "../../supabase/functions/_shared/fitnessWorkoutFinishDomain";

type RecoveryRow = Pick<Tables<"workout_sessions">, "id" | "user_id" | "started_at" | "completed_at" | "source" | "exercises">;
const DAY = 86_400_000;
const groups: Readonly<Record<string, string>> = {
  chest: "chest", back: "back", shoulders: "shoulders", biceps: "biceps", triceps: "triceps",
  legs: "legs", quads: "legs", hamstrings: "legs", "upper legs": "legs",
  core: "core", abs: "core", glutes: "glutes", calves: "calves",
};
const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
const number = (value: unknown, max: number) => typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= max;

/** Only confirmed gym exercises with valid completed sets affect their explicit muscle group. */
export function bodyMapRecovery(rows: readonly RecoveryRow[], ownerId: string, now = new Date()): Record<string, MuscleStatus> {
  const last = new Map<string, number>(), nowMs = now.getTime();
  if (!Number.isFinite(nowMs)) return {};
  for (const row of rows) {
    if (!row || row.user_id !== ownerId || getSessionKind(row) !== "gym" || !row.completed_at || !row.started_at) continue;
    const completed = Date.parse(row.completed_at), started = Date.parse(row.started_at);
    if (!Number.isFinite(completed) || !Number.isFinite(started) || started > completed || completed > nowMs
      || completed < nowMs - 14 * DAY || !Array.isArray(row.exercises) || row.exercises.length > LIMITS.exercises) continue;
    for (const exercise of row.exercises) {
      if (!object(exercise) || typeof exercise.name !== "string" || !exercise.name.trim() || exercise.name.length > LIMITS.name
        || typeof exercise.muscle !== "string" || exercise.muscle.length > LIMITS.muscle || typeof exercise.timed !== "boolean"
        || !Array.isArray(exercise.sets) || !exercise.sets.length || exercise.sets.length > LIMITS.sets) continue;
      const key = exercise.muscle.trim().toLowerCase();
      const group = Object.prototype.hasOwnProperty.call(groups, key) ? groups[key] : undefined;
      if (!group || !exercise.sets.every(set => object(set) && set.done === true && number(set.weight, LIMITS.weight)
        && typeof set.reps === "number" && Number.isSafeInteger(set.reps) && set.reps >= 1 && set.reps <= LIMITS.reps
        && (set.restSeconds === undefined || number(set.restSeconds, LIMITS.rest_seconds))
        && (set.completedAt === undefined || typeof set.completedAt === "string" && Number.isFinite(Date.parse(set.completedAt))
          && Date.parse(set.completedAt) >= started && Date.parse(set.completedAt) <= completed))) continue;
      last.set(group, Math.max(last.get(group) ?? 0, completed));
    }
  }
  const statuses: Record<string, MuscleStatus> = {};
  for (const [group, stamp] of last) {
    const days = (nowMs - stamp) / DAY;
    statuses[group] = days < 1 ? "needsRest" : days < 2 ? "recovering" : "ready";
  }
  return statuses;
}

/** Account-bound paginated read of the same JSON stored by the active workout writer. */
export async function readBodyMapRecovery(ownerId: string, assertCurrent: () => void): Promise<Record<string, MuscleStatus>> {
  const now = new Date(), rows: RecoveryRow[] = [];
  for (let offset = 0; ; offset += 500) {
    assertCurrent();
    const { data, error } = await supabase.from("workout_sessions")
      .select("id,user_id,started_at,completed_at,source,exercises").eq("user_id", ownerId)
      .not("completed_at", "is", null).gte("completed_at", new Date(now.getTime() - 14 * DAY).toISOString())
      .lte("completed_at", now.toISOString()).order("completed_at", { ascending: false }).order("id", { ascending: false }).range(offset, offset + 499);
    assertCurrent();
    if (error || !Array.isArray(data)) throw error ?? new Error("Missing body-map recovery");
    if (data.some(row => !row || row.user_id !== ownerId)) throw new Error("Body-map recovery owner changed");
    rows.push(...data);
    if (data.length < 500) return bodyMapRecovery(rows, ownerId, now);
  }
}
